import clsx from 'clsx';
import { useCallback, useState } from 'react';

import { ModalContext } from 'src/contexts/modal.context';

import classes from 'src/components/Modal/ModalWrap.module.scss';
import { ConfirmationProps } from 'src/contexts/resizableWindow.context';
import { Maybe } from 'src/models';
import { Confirmation } from 'src/components/Confirmation';

interface Props {
  onClose: () => void;
  isCloseOnBackdrop?: boolean;
  setVisibility: (isVisible: boolean) => void;
  children: JSX.Element;
}

export function ModalWrap({ children, onClose, isCloseOnBackdrop = true, setVisibility }: Props) {
  const [confirmationProps, setConfirmationProps] = useState<Maybe<ConfirmationProps>>();

  const [onCloseConfirmationFn, setOnCloseConfirmationFn] = useState<Maybe<() => void>>();

  const isDisabledClosing = !!confirmationProps;

  const handleCloseWindow = useCallback(
    (isSkipConfirmation?: boolean) => {
      if (isDisabledClosing && !isSkipConfirmation) setOnCloseConfirmationFn(() => onClose);
      else onClose();
    },
    [isDisabledClosing, onClose],
  );

  const showConfirmation = useCallback(
    (onCloseFn: () => void) => {
      if (confirmationProps) setOnCloseConfirmationFn(() => onCloseFn);
    },
    [confirmationProps],
  );

  const closeConfirmation = useCallback(() => setOnCloseConfirmationFn(undefined), []);

  const removeConfirmation = useCallback(() => {
    setOnCloseConfirmationFn(undefined);
    setConfirmationProps(undefined);
  }, []);

  const handleOnConfirm = useCallback(() => {
    if (confirmationProps && onCloseConfirmationFn) {
      confirmationProps.onConfirm(onCloseConfirmationFn);
      removeConfirmation();
    }
  }, [confirmationProps, onCloseConfirmationFn, removeConfirmation]);

  const handleOnDiscard = useCallback(() => {
    if (onCloseConfirmationFn) {
      removeConfirmation();
      onCloseConfirmationFn();
    }
  }, [onCloseConfirmationFn, removeConfirmation]);

  const renderConfirmation = useCallback(() => {
    if (onCloseConfirmationFn && confirmationProps) {
      return (
        <Confirmation
          {...confirmationProps}
          onConfirm={handleOnConfirm}
          onCancel={closeConfirmation}
          onDiscard={handleOnDiscard}
        />
      );
    }
  }, [closeConfirmation, confirmationProps, handleOnConfirm, handleOnDiscard, onCloseConfirmationFn]);

  function handleOnBackdropClick() {
    if (isCloseOnBackdrop) handleCloseWindow();
  }

  return (
    <ModalContext.Provider
      value={{
        closeModal: handleCloseWindow,
        setVisibility,
        setConfirmationProps,
        showConfirmation,
        renderConfirmation,
        isDisabledClosing,
      }}
    >
      <div className={clsx('center ignore-onclick-outside', classes.container)}>
        <div className={classes.backdrop} onClick={handleOnBackdropClick} />
        {children}
      </div>
    </ModalContext.Provider>
  );
}
